import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import CustomDatePicker from '../shared/CustomDatePicker';
import { Search, SlidersHorizontal, RefreshCw, ShoppingBag, CheckCircle, Circle, X, ArrowRight, Package, Trash2, Check, Clock, LayoutGrid, LayoutList, GripVertical, Route, Save, RotateCcw, Bookmark } from 'lucide-react';
import SalesOrderCard from './SalesOrderCard';
import EditOrderModal from './EditOrderModal';
import RecordSaleModal from './RecordSaleModal';
import CancelOrderModal from '../shared/CancelOrderModal';
import OrderDetailDrawer from '../shared/OrderDetailDrawer';
import Pagination from '../../../../components/common/Pagination';
import { searchOrders, getAllOrders, executeTransition, executeBulkTransition, getWorkflowByPlan, getOrderTabCounts, getRouteSequence, updateDeliverySequences, upsertRouteSequence } from '../../../../services/api/salesOrderApi';
import { getCurrentTenant } from '../../../../services/api/companyApi';
import { useAuth } from '../../../../context/AuthContext';
import { getStatusColor, getStatusLabel } from '../../../../config/orderStatus';
import { formatDateTime } from '../../../../utils/dateUtils';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown, User as UserIcon, Layers, ChevronRight, CheckSquare, Truck } from 'lucide-react';
import { getAllUsers } from '../../../../services/api/userApi';
import { normalizeRole } from '../../../../config/roles';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2
    }).format(amount || 0);

export const TABS = [
    { id: 'new_order', label: 'New Order', statuses: ['CREATED', 'NEW_ORDER', 'PROCESSING', 'PENDING', 'ON_HOLD', 'UNDER_REVIEW', 'REJECTED', 'REQUEST_FOR_MODIFICATION'], accent: 'bg-emerald-500', countColor: 'text-emerald-600 bg-emerald-50' },
    { id: 'acknowledged', label: 'Acknowledged', statuses: ['ACKNOWLEDGED'], accent: 'bg-blue-500', countColor: 'text-blue-600 bg-blue-50' },
    { id: 'warehouse', label: 'Warehouse', statuses: ['SENT_TO_WAREHOUSE', 'PENDING_WAREHOUSE', 'IN_WAREHOUSE'], accent: 'bg-orange-500', countColor: 'text-orange-600 bg-orange-50' },
    { id: 'ready', label: 'Ready', statuses: ['APPROVED', 'REVISED', 'READY', 'PACKED', 'CONFIRMED'], accent: 'bg-blue-500', countColor: 'text-blue-600 bg-blue-50' },
    { id: 'in_delivery', label: 'In Delivery', statuses: ['DISPATCHED', 'DESPATCHED', 'OUT_FOR_DELIVERY'], accent: 'bg-indigo-500', countColor: 'text-indigo-600 bg-indigo-50' },
    { id: 'completed', label: 'Completed', statuses: ['DELIVERED', 'COMPLETED', 'FINISHED'], accent: 'bg-emerald-600', countColor: 'text-emerald-700 bg-emerald-50' },
    { id: 'cancelled', label: 'Cancelled', statuses: ['CANCELLED', 'RETURNED', 'REFUNDED'], accent: 'bg-red-500', countColor: 'text-red-600 bg-red-50' }
];

const SalesOrdersView = ({ role = 'SALES_PERSON', onNavigate, initialTab = 'new_order' }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingTransition, setLoadingTransition] = useState(null); // id or 'bulk'
    const [error, setError] = useState(null);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [workflow, setWorkflow] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState('grid');       // 'grid' | 'list'
    const [selectedOrder, setSelectedOrder] = useState(null); // for detail drawer
    const [editModalOrder, setEditModalOrder] = useState(null);
    const [cancelModalOrder, setCancelModalOrder] = useState(null);
    const [cancelModalTransition, setCancelModalTransition] = useState(null);
    const [recordSaleOrder, setRecordSaleOrder] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);

    // Date filtering
    const [dateFilterType, setDateFilterType] = useState('ALL'); // 'TODAY' | 'ALL' | 'CUSTOM'
    const [userHasSetFilter, setUserHasSetFilter] = useState(false); // tracks if user explicitly chose a filter
    const [customDateRange, setCustomDateRange] = useState({
        start: format(new Date(), 'yyyy-MM-dd'),
        end: format(new Date(), 'yyyy-MM-dd')
    });

    // Tab badge counts (statusCode → count from backend)
    const [tabCounts, setTabCounts] = useState({});
    const [absoluteTabCounts, setAbsoluteTabCounts] = useState({});

    // Salesperson filter state
    const [salespeople, setSalespeople] = useState([]);
    const [selectedSalesPersonId, setSelectedSalesPersonId] = useState('all');
    const [loadingSalespeople, setLoadingSalespeople] = useState(false);

    const currentTab = TABS.find(t => t.id === activeTab);
    const isAdmin = ['TENANT_OWNER', 'TENANT_ADMIN', 'SALES_MANAGER', 'SYSTEM_ADMIN'].includes(normalizeRole(role) || role);

    const [groupBy, setGroupBy] = useState(() => localStorage.getItem('yaseero_orders_groupby') || 'none');
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const isGroupingActive = isAdmin && groupBy !== 'none';

    // Route Planner state
    const [routePlannerMode, setRoutePlannerMode] = useState(false);
    const [savedRoute, setSavedRoute] = useState([]); // persistent route from DB
    const [routeOrderedItems, setRouteOrderedItems] = useState([]); // locally reordered items
    const [routeSequenceDirty, setRouteSequenceDirty] = useState(false); // unsaved changes
    const [savingSequence, setSavingSequence] = useState(false);
    const [savingRoute, setSavingRoute] = useState(false);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    // Loading Checklist state
    const [checklistMode, setChecklistMode] = useState(false);
    const [verifiedQuantities, setVerifiedQuantities] = useState(() => {
        try {
            const saved = localStorage.getItem(`yaseero_checklist_${activeTab}`);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });

    useEffect(() => {
        try {
            const saved = localStorage.getItem(`yaseero_checklist_${activeTab}`);
            setVerifiedQuantities(saved ? JSON.parse(saved) : {});
        } catch (e) {
            setVerifiedQuantities({});
        }
    }, [activeTab]);

    const updateVerifiedQty = (itemKey, val) => {
        setVerifiedQuantities(prev => {
            const next = { ...prev, [itemKey]: val };
            localStorage.setItem(`yaseero_checklist_${activeTab}`, JSON.stringify(next));
            return next;
        });
    };

    const resetChecklist = () => {
        setVerifiedQuantities({});
        localStorage.removeItem(`yaseero_checklist_${activeTab}`);
    };

    useEffect(() => {
        if (viewMode !== 'items') {
            setChecklistMode(false);
        }
    }, [viewMode]);

    const handleGroupByChange = (val) => {
        setGroupBy(val);
        localStorage.setItem('yaseero_orders_groupby', val);
        setCollapsedGroups({});
    };

    const handleScroll = (e) => {
        if (viewMode === 'items' || (isAdmin && groupBy !== 'none')) return; // Infinite scroll disabled when grouping or items summary is active
        if (loading || loadingMore || currentPage >= totalPages) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            setCurrentPage(prev => prev + 1);
        }
    };

    // Reset page to 1 when filters/search/groupby change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, dateFilterType, customDateRange.start, customDateRange.end, groupBy, viewMode]);

    // Fetch orders
    const fetchOrders = useCallback(async () => {
        const isGroupingActive = isAdmin && groupBy !== 'none';
        if (currentPage === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);
        setSelectedOrderIds([]); // Clear selection on refresh
        try {
            const params = {
                page: currentPage - 1,
                size: (isGroupingActive || viewMode === 'items') ? 1000 : pageSize,
                sort: 'updatedAt,desc'
            };

            const filters = {};
            if (currentTab?.statuses?.length > 0) {
                filters.statusCodes = currentTab.statuses;
            }
            if (searchTerm) {
                filters.searchTerm = searchTerm;
            }

            // Apply Date Filters
            if (dateFilterType === 'TODAY') {
                const today = format(new Date(), 'yyyy-MM-dd');
                filters.startDate = today;
                filters.endDate = today;
            } else if (dateFilterType === 'RANGE' && customDateRange.start && customDateRange.end) {
                filters.startDate = customDateRange.start;
                filters.endDate = customDateRange.end;
            }

            let result;
            if (isAdmin) {
                // To keep filtering available for admin, we use searchOrders without salesPerson filter
                // UNLESS the admin has specifically chosen a salesperson filter
                if (selectedSalesPersonId !== 'all') {
                    filters.salesPersonPublicId = selectedSalesPersonId;
                }
                result = await searchOrders(filters, params);
            } else {
                const salesPersonId = user?.publicId || user?.id;
                if (salesPersonId) filters.salesPersonPublicId = salesPersonId;
                result = await searchOrders(filters, params);
            }
            const o = result.orders || [];
            
            if (currentPage === 1) {
                setOrders(o);
            } else {
                setOrders(prev => {
                    const existingIds = new Set(prev.map(item => item.id));
                    const newItems = o.filter(item => !existingIds.has(item.id));
                    return [...prev, ...newItems];
                });
            }
            
            setTotalElements(result.totalElements || 0);
            setTotalPages(isGroupingActive ? 1 : (result.totalPages || 0));

        } catch (err) {
            console.error('Failed to load orders:', err);
            setError('Failed to load orders. Please try again.');
            setOrders([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [user, isAdmin, currentPage, pageSize, currentTab, searchTerm, dateFilterType, customDateRange.start, customDateRange.end, selectedSalesPersonId, groupBy]);

    // Fetch tab counts (independent of pagination / search)
    const fetchTabCounts = useCallback(async () => {
        let startDate = null;
        let endDate = null;

        if (dateFilterType === 'TODAY') {
            startDate = format(new Date(), 'yyyy-MM-dd');
            endDate = format(new Date(), 'yyyy-MM-dd');
        } else if (dateFilterType === 'CUSTOM') {
            startDate = customDateRange.start;
            endDate = customDateRange.end;
        }

        const salesPersonPublicId = isAdmin 
            ? (selectedSalesPersonId !== 'all' ? selectedSalesPersonId : null)
            : (user?.publicId || user?.id);

        const params = { 
            startDate, 
            endDate, 
            salesPersonPublicId 
        };
        if (searchTerm) {
            params.searchTerm = searchTerm;
        }

        // Fetch filtered counts and absolute totals in parallel
        const [filtered, absolute] = await Promise.all([
            getOrderTabCounts(params),
            getOrderTabCounts({
                salesPersonPublicId: isAdmin ? null : salesPersonPublicId
            })
        ]);

        setTabCounts(filtered);
        setAbsoluteTabCounts(absolute);
    }, [isAdmin, user, dateFilterType, customDateRange.start, customDateRange.end, selectedSalesPersonId, searchTerm]);

    // On tab switch: only apply a smart default if the user has NOT explicitly set a filter.
    // Terminal tabs (completed/cancelled) default to TODAY to prevent loading all history;
    // all other tabs default to ALL. Once the user picks a filter manually, it persists across tabs.
    useEffect(() => {
        if (!userHasSetFilter) {
            const isTerminal = ['completed', 'cancelled'].includes(activeTab);
            setDateFilterType(isTerminal ? 'TODAY' : 'ALL');
        }
        setCurrentPage(1);
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchTabCounts();
    }, [fetchTabCounts]);

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 300);
        return () => clearTimeout(delay);
    }, [fetchOrders, dateFilterType, customDateRange.start, customDateRange.end]);



    // Fetch workflow once on mount
    useEffect(() => {
        const loadWorkflow = async () => {
            try {
                // Try to get tenant's plan, fallback to BASIC
                let plan = 'BASIC';
                try {
                    const tenant = await getCurrentTenant();
                    plan = tenant.subscriptionPlan || 'BASIC';
                } catch (e) {
                    console.warn('Failed to fetch tenant plan, using default BASIC');
                }
                const wf = await getWorkflowByPlan(plan);
                setWorkflow(wf);
            } catch (err) {
                console.error('Failed to load workflow mapping:', err);
            }
        };
        loadWorkflow();
    }, []);

    // Fetch salespeople for filter (Admin only)
    useEffect(() => {
        if (isAdmin) {
            const fetchSalespeople = async () => {
                setLoadingSalespeople(true);
                try {
                    // Fetch all users for the tenant and filter locally to ensure we catch everyone
                    // who might have a salesperson-related role (SALES_PERSON, SALES_MANAGER, etc.)
                    const result = await getAllUsers({ size: 2000 });
                    const users = result.content || [];
                    
                    const salesRoles = ['SALES_PERSON', 'SALESMAN', 'SALES_MANAGER', 'TENANT_OWNER', 'TENANT_ADMIN', 'ADMIN', 'SYSTEM_ADMIN'];
                    
                    const filtered = users.filter(u => {
                        // If roles are present, check if any of them are sales-related
                        if (u.realmRoles && u.realmRoles.length > 0) {
                            return u.realmRoles.some(r => salesRoles.includes(r.toUpperCase()));
                        }
                        // Fallback: if no roles are explicitly mapped but it's an admin/staff
                        return true; 
                    });
                    
                    console.log(`[SalesOrdersView] Found ${users.length} total users, ${filtered.length} matching sales roles`);
                    setSalespeople(filtered);
                } catch (err) {
                    console.error('Failed to fetch salespeople:', err);
                } finally {
                    setLoadingSalespeople(false);
                }
            };
            fetchSalespeople();
        }
    }, [isAdmin]);

    // For In Delivery tab: sort by deliverySequence (today's) → savedRoute preference → updatedAt.
    // This is computed synchronously so cards always reflect the correct order immediately.
    const sortedInDeliveryOrders = useMemo(() => {
        if (activeTab !== 'in_delivery') return orders;

        const routeMap = {};
        savedRoute.forEach(r => { routeMap[r.customerPublicId] = r.sequenceNumber; });

        return [...orders].sort((a, b) => {
            const aDaySeq = a.deliverySequence;
            const bDaySeq = b.deliverySequence;

            // Both have today's sequence → sort ascending
            if (aDaySeq != null && bDaySeq != null) return aDaySeq - bDaySeq;
            if (aDaySeq != null) return -1;
            if (bDaySeq != null) return 1;

            // Fall back to saved route preference
            const aRouteSeq = routeMap[a.customer?.publicId] ?? 9999;
            const bRouteSeq = routeMap[b.customer?.publicId] ?? 9999;
            if (aRouteSeq !== bRouteSeq) return aRouteSeq - bRouteSeq;

            // Final fallback: updatedAt desc
            return new Date(b.updatedAt) - new Date(a.updatedAt);
        });
    }, [orders, savedRoute, activeTab]);

    // filteredOrders feeds groupedOrders and all list/card rendering
    const filteredOrders = activeTab === 'in_delivery' ? sortedInDeliveryOrders : orders;

    // ── Route Planner: fetch saved route when entering In Delivery tab ──────────
    useEffect(() => {
        if (activeTab === 'in_delivery') {
            const fetchRoute = async () => {
                try {
                    const spId = !isAdmin ? (user?.publicId || user?.id) : (selectedSalesPersonId !== 'all' ? selectedSalesPersonId : null);
                    const route = await getRouteSequence(spId);
                    setSavedRoute(route);
                } catch (err) {
                    console.warn('Failed to fetch route sequence:', err);
                    setSavedRoute([]);
                }
            };
            fetchRoute();
        } else {
            // Exit route planner when switching tabs
            setRoutePlannerMode(false);
            setRouteSequenceDirty(false);
        }
    }, [activeTab, isAdmin, user, selectedSalesPersonId]);

    // ── Sync routeOrderedItems (drag list) from the computed sorted orders ───────
    useEffect(() => {
        if (activeTab === 'in_delivery') {
            setRouteOrderedItems(sortedInDeliveryOrders);
        }
    }, [sortedInDeliveryOrders, activeTab]);

    // ── Route Planner drag-and-drop handlers (mouse + touch) ──────────────────
    const handleDragStart = useCallback((e, idx) => {
        dragItem.current = idx;
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragEnter = useCallback((e, idx) => {
        dragOverItem.current = idx;
        e.preventDefault();
    }, []);

    const handleDrop = useCallback(() => {
        if (dragItem.current === null || dragOverItem.current === null) return;
        if (dragItem.current === dragOverItem.current) return;

        setRouteOrderedItems(prev => {
            const copy = [...prev];
            const [moved] = copy.splice(dragItem.current, 1);
            copy.splice(dragOverItem.current, 0, moved);
            dragItem.current = null;
            dragOverItem.current = null;
            return copy;
        });
        setRouteSequenceDirty(true);
    }, []);

    // Touch drag support (tablet/mobile)
    const touchDragItem = useRef(null);
    const touchDragClone = useRef(null);

    const handleTouchStart = useCallback((e, idx) => {
        touchDragItem.current = idx;
        dragItem.current = idx;
        // Create a visual clone so the user can see what they're dragging
        const el = e.currentTarget;
        const clone = el.cloneNode(true);
        const rect = el.getBoundingClientRect();
        clone.style.cssText = `
            position: fixed;
            left: ${rect.left}px;
            top: ${rect.top}px;
            width: ${rect.width}px;
            opacity: 0.85;
            pointer-events: none;
            z-index: 9999;
            background: white;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0,0,0,0.15);
            transform: scale(1.02);
        `;
        document.body.appendChild(clone);
        touchDragClone.current = clone;
    }, []);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        if (!touchDragClone.current) return;
        const touch = e.touches[0];
        // Move the visual clone
        const clone = touchDragClone.current;
        clone.style.left = `${touch.clientX - 20}px`;
        clone.style.top  = `${touch.clientY - 24}px`;
        // Find which row the finger is hovering over
        clone.style.display = 'none';
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        clone.style.display = '';
        if (!el) return;
        const row = el.closest('[data-route-idx]');
        if (row) {
            const overIdx = parseInt(row.dataset.routeIdx, 10);
            if (!isNaN(overIdx)) dragOverItem.current = overIdx;
        }
    }, []);

    const handleTouchEnd = useCallback(() => {
        // Remove visual clone
        if (touchDragClone.current) {
            document.body.removeChild(touchDragClone.current);
            touchDragClone.current = null;
        }
        handleDrop();
        touchDragItem.current = null;
    }, [handleDrop]);

    // ── Save today's sequence ─────────────────────────────────────────────────
    const handleSaveTodaySequence = useCallback(async () => {
        setSavingSequence(true);
        try {
            const sequences = routeOrderedItems.map((order, idx) => ({
                orderPublicId: order.publicId || order.id,
                sequenceNumber: idx + 1,
            }));
            await updateDeliverySequences(sequences);
            // Update local orders with new sequence
            setOrders(prev => {
                const seqMap = {};
                sequences.forEach(s => { seqMap[s.orderPublicId] = s.sequenceNumber; });
                return prev.map(o => ({
                    ...o,
                    deliverySequence: seqMap[o.publicId || o.id] ?? o.deliverySequence
                }));
            });
            setRouteSequenceDirty(false);
        } catch (err) {
            setError(`Failed to save sequence: ${err?.response?.data?.message || 'Please try again.'}`);
        } finally {
            setSavingSequence(false);
        }
    }, [routeOrderedItems]);

    // ── Save as default route ─────────────────────────────────────────────────
    const handleSaveDefaultRoute = useCallback(async () => {
        setSavingRoute(true);
        try {
            const sequences = routeOrderedItems
                .filter(o => o.customer?.publicId)
                .map((order, idx) => ({
                    customerPublicId: order.customer.publicId,
                    sequenceNumber: idx + 1,
                }));
            const spId = !isAdmin ? null : (selectedSalesPersonId !== 'all' ? selectedSalesPersonId : null);
            await upsertRouteSequence(sequences, spId);
            // Refresh saved route
            const spIdFetch = !isAdmin ? (user?.publicId || user?.id) : (selectedSalesPersonId !== 'all' ? selectedSalesPersonId : null);
            const newRoute = await getRouteSequence(spIdFetch);
            setSavedRoute(newRoute);
        } catch (err) {
            setError(`Failed to save route: ${err?.response?.data?.message || 'Please try again.'}`);
        } finally {
            setSavingRoute(false);
        }
    }, [routeOrderedItems, isAdmin, selectedSalesPersonId, user]);

    // ── Reset to default route ────────────────────────────────────────────────
    const handleResetToDefaultRoute = useCallback(() => {
        const routeMap = {};
        savedRoute.forEach(r => { routeMap[r.customerPublicId] = r.sequenceNumber; });

        setRouteOrderedItems(prev => {
            return [...prev].sort((a, b) => {
                const aSeq = routeMap[a.customer?.publicId] ?? 9999;
                const bSeq = routeMap[b.customer?.publicId] ?? 9999;
                return aSeq - bSeq;
            });
        });
        setRouteSequenceDirty(false);
    }, [savedRoute]);


    // Helper to get delivery date grouping label without local timezone shifts
    const getDeliveryDateGroupLabel = useCallback((deliveryDateStr) => {
        if (!deliveryDateStr) return 'No Delivery Date';
        try {
            let date;
            if (deliveryDateStr.includes('T')) {
                date = new Date(deliveryDateStr);
            } else {
                const parts = deliveryDateStr.split('-');
                if (parts.length === 3) {
                    date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                } else {
                    date = new Date(deliveryDateStr);
                }
            }
            
            if (isNaN(date.getTime())) return 'No Delivery Date';
            
            const today = new Date();
            const tomorrow = new Date();
            tomorrow.setDate(today.getDate() + 1);
            
            const formatDateKey = (d) => {
                const yyyy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            };
            
            const orderDateStrNormalized = formatDateKey(date);
            const todayStrNormalized = formatDateKey(today);
            const tomorrowStrNormalized = formatDateKey(tomorrow);
            
            if (orderDateStrNormalized === todayStrNormalized) {
                return `Today · ${format(date, 'dd MMM')}`;
            } else if (orderDateStrNormalized === tomorrowStrNormalized) {
                return `Tomorrow · ${format(date, 'dd MMM')}`;
            } else {
                return format(date, 'dd MMM yyyy');
            }
        } catch (e) {
            console.error('Failed parsing date:', e);
            return 'No Delivery Date';
        }
    }, []);

    // Grouping Logic to segment filteredOrders by salesperson or deliveryDate
    const groupedOrders = useMemo(() => {
        if (!isAdmin || groupBy === 'none') {
            return { 'All Orders': filteredOrders };
        }
        
        const groups = {};
        
        filteredOrders.forEach(order => {
            let key = '';
            if (groupBy === 'salesperson') {
                key = order.salesman || 'Unassigned';
            } else if (groupBy === 'deliveryDate') {
                key = getDeliveryDateGroupLabel(order.deliveryDate);
            } else {
                key = 'All Orders';
            }
            
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(order);
        });
        
        // Sort groups alphabetically for salespersons (Unassigned last)
        // or chronologically for delivery dates (No Delivery Date last)
        const sortedKeys = Object.keys(groups).sort((a, b) => {
            if (groupBy === 'salesperson') {
                if (a === 'Unassigned') return 1;
                if (b === 'Unassigned') return -1;
                return a.localeCompare(b);
            }
            if (groupBy === 'deliveryDate') {
                if (a === 'No Delivery Date') return 1;
                if (b === 'No Delivery Date') return -1;
                
                const dateA = groups[a][0]?.deliveryDate;
                const dateB = groups[b][0]?.deliveryDate;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return new Date(dateA) - new Date(dateB);
            }
            return 0;
        });
        
        const sortedGroups = {};
        sortedKeys.forEach(k => {
            sortedGroups[k] = groups[k];
        });
        
        return sortedGroups;
    }, [filteredOrders, groupBy, getDeliveryDateGroupLabel, isAdmin]);

    // Helper to determine if an order can be edited
    const canEditOrder = useCallback((order) => {
        if (!order) return false;
        const EDITABLE_STATUSES = ['CREATED', 'NEW_ORDER', 'ACKNOWLEDGED', 'REJECTED', 'REQUEST_FOR_MODIFICATION'];
        if (!EDITABLE_STATUSES.includes(order.status)) return false;

        const isCreator = order.assignedSalesPersonPublicId === (user?.publicId || user?.id);
        const isManager = ['SALES_MANAGER', 'TENANT_ADMIN', 'TENANT_OWNER', 'SYSTEM_ADMIN'].includes(normalizeRole(role) || role);
        
        return isCreator || isManager;
    }, [user, role]);

    // Local Transition Mapping Logic
    const getAvailableTransitionsForOrder = useCallback((order) => {
        if (!workflow || !order || !order.status) return [];

        const normalize = (r) => {
            if (!r) return 'USER';
            const norm = r.toUpperCase().replace('ROLE_', '');
            if (norm === 'SALESMAN') return 'SALES_PERSON';
            if (norm === 'WAREHOUSE') return 'WAREHOUSE_PERSON';
            return norm;
        };

        const userRole = normalize(role);

        return workflow.transitions.filter(t => {
            // SPECIAL CASE: Cancellation transition (to status CANCELLED)
            const isCancelTransition = t.toStatus?.statusCode === 'CANCELLED';
            if (isCancelTransition) {
                if (order.status !== 'CREATED') return false;
                const statusMatch = t.fromStatus.statusCode === order.status ||
                    (t.fromStatus.statusCode === 'DISPATCHED' && order.status === 'DESPATCHED');
                if (!statusMatch) return false;

                const isAssignedSalesperson = (order.assignedSalesPersonPublicId === (user?.publicId || user?.id)) ||
                    (order.delegatedSalesPersonPublicId === (user?.publicId || user?.id));
                const isManagerOrAdmin = ['SALES_MANAGER', 'TENANT_ADMIN', 'TENANT_OWNER', 'ADMIN', 'SYSTEM_ADMIN'].includes(userRole);
                const isSalesPersonAllowed = userRole === 'SALES_PERSON' && isAssignedSalesperson;
                return isManagerOrAdmin || isSalesPersonAllowed;
            }

            // SPECIAL CASE: Reactivate Order (CANCELLED -> CREATED)
            const isReactivateTransition = t.fromStatus.statusCode === 'CANCELLED' && t.toStatus.statusCode === 'CREATED';
            if (isReactivateTransition) {
                if (order.status !== 'CANCELLED') return false;
                
                const isCanceller = order.cancelledBy && user && order.cancelledBy === user.username;
                const isManagerOrAdmin = ['SALES_MANAGER', 'TENANT_ADMIN', 'TENANT_OWNER', 'ADMIN', 'SYSTEM_ADMIN'].includes(userRole);
                const isAssignedSalesperson = (order.assignedSalesPersonPublicId === (user?.publicId || user?.id)) ||
                    (order.delegatedSalesPersonPublicId === (user?.publicId || user?.id));
                const isSalesPersonAllowed = userRole === 'SALES_PERSON' && isAssignedSalesperson;
                return isCanceller || isManagerOrAdmin || isSalesPersonAllowed;
            }

            // Match status
            const statusMatch = t.fromStatus.statusCode === order.status ||
                (t.fromStatus.statusCode === 'DISPATCHED' && order.status === 'DESPATCHED');
            if (!statusMatch) return false;

            // Privileged roles see all buttons for the matched status, except warehouse actions if not a warehouse person
            const privilegedRoles = ['SUPPLIER', 'SYSTEM_ADMIN', 'TENANT_OWNER', 'ADMIN', 'SYSTEM ADMIN'];
            if (privilegedRoles.includes(userRole)) {
                const actionName = (t.transitionName || t.label || t.name || t.action || '').toLowerCase();
                const isWarehouseSpecific = ((actionName.includes('revise') || actionName.includes('revision')) && !actionName.includes('resubmit') && !actionName.includes('accept')) || actionName.includes('reject');
                if (isWarehouseSpecific && userRole !== 'WAREHOUSE_PERSON') {
                    return false;
                }
                return true;
            }

            // Match role
            const isAllowedRole = t.allowedRoles.some(r => {
                const normR = normalize(r);
                if (userRole === 'SALES_MANAGER' && normR === 'SALES_PERSON') return true;
                return normR === userRole || normR === 'USER';
            });

            if (isAllowedRole) return true;

            // SPECIAL CASE: If i am the assigned salesperson, let me Acknowledge or Cancel even if not strictly in workflow roles
            const isAssignedSalesperson = (order.assignedSalesPersonPublicId === (user?.publicId || user?.id)) ||
                (order.delegatedSalesPersonPublicId === (user?.publicId || user?.id));

            if (userRole === 'SALES_PERSON' && isAssignedSalesperson) {
                const actionName = (t.transitionName || t.label || t.name || t.action || '').toLowerCase();
                const toStatus = (t.toStatus?.statusCode || '').toLowerCase();

                // They can do everything except mark items as ready in the warehouse stage
                const isMarkReadyAction = actionName.includes('ready') || actionName.includes('approve') || toStatus.includes('ready') || toStatus.includes('approved');
                const isInWarehouseStage = ['sent_to_warehouse', 'pending_warehouse', 'in_warehouse'].includes(order.status?.toLowerCase());

                if (isInWarehouseStage && isMarkReadyAction) {
                    return false;
                }

                return true;
            }

            return false;
        });
    }, [workflow, role]);

    // Selection Handlers
    const toggleSelect = (orderId) => {
        setSelectedOrderIds(prev =>
            prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
        );
    };

    const toggleSelectAll = async () => {
        if (selectedOrderIds.length === totalElements && totalElements > 0) {
            setSelectedOrderIds([]);
        } else {
            try {
                const filters = {};
                if (currentTab?.statuses?.length > 0) {
                    filters.statusCodes = currentTab.statuses;
                }
                if (searchTerm) {
                    filters.searchTerm = searchTerm;
                }
                if (dateFilterType === 'TODAY') {
                    const today = format(new Date(), 'yyyy-MM-dd');
                    filters.startDate = today;
                    filters.endDate = today;
                } else if (dateFilterType === 'RANGE' && customDateRange.start && customDateRange.end) {
                    filters.startDate = customDateRange.start;
                    filters.endDate = customDateRange.end;
                }
                
                const params = {
                    page: 0,
                    size: 10000,
                    sort: 'updatedAt,desc'
                };
                
                let result;
                if (isAdmin) {
                    if (selectedSalesPersonId !== 'all') {
                        filters.salesPersonPublicId = selectedSalesPersonId;
                    }
                    result = await searchOrders(filters, params);
                } else {
                    const salesPersonId = user?.publicId || user?.id;
                    if (salesPersonId) filters.salesPersonPublicId = salesPersonId;
                    result = await searchOrders(filters, params);
                }
                
                const allOrders = result.orders || [];
                setSelectedOrderIds(allOrders.map(o => o.id));
            } catch (err) {
                console.error('Failed to select all orders:', err);
                setError('Failed to select all orders. Please try again.');
            }
        }
    };

    // Determine common transitions for bulk actions
    const commonTransitions = useMemo(() => {
        if (selectedOrderIds.length === 0 || !workflow) return [];

        const selectedOrders = orders.filter(o => selectedOrderIds.includes(o.id));
        const allTMap = selectedOrders.map(o => getAvailableTransitionsForOrder(o));

        if (allTMap.length === 0) return [];

        const firstOrderTs = allTMap[0];
        const shared = firstOrderTs.filter(t1 =>
            allTMap.every(orderTs =>
                orderTs.some(t2 => t2.transitionName === t1.transitionName)
            )
        );

        const getCanonicalId = (item) => {
            const raw = (item.transitionName || item.label || item.name || item.action || '').toLowerCase();
            if (raw.includes('deliver')) return 'deliver';
            if (raw.includes('warehouse')) return 'warehouse';
            if (raw.includes('cancel')) return 'cancel';
            if (raw.includes('reject')) return 'reject';
            if (raw.includes('return')) return 'return';
            if (raw.includes('approve')) return 'approve';
            if (raw.includes('ready')) return 'ready';
            return raw.replace(/\bas\b/g, '').trim();
        };

        return shared.reduce((uniq, t) => {
            if (!uniq.some(ext => getCanonicalId(ext) === getCanonicalId(t))) {
                uniq.push(t);
            }
            return uniq;
        }, []);
    }, [selectedOrderIds, orders, workflow, getAvailableTransitionsForOrder]);

    const handleCancelSubmit = async (order, transition, payload) => {
        setCancelModalOrder(null);
        setCancelModalTransition(null);
        await handleTransition(order, transition, payload);
    };

    // Handle workflow transition
    const handleTransition = async (order, transition, payload = {}) => {
        const isCancel = transition.toStatus?.statusCode === 'CANCELLED';
        if (isCancel && !payload.notes) {
            setCancelModalOrder(order);
            setCancelModalTransition(transition);
            return;
        }

        setLoadingTransition(order.id);
        setError(null);
        try {
            const updated = await executeTransition(order.publicId || order.id, transition.publicId || transition.id, payload);

            // If the updated order's status no longer belongs to the current tab, remove it from the local list
            const stillInTab = currentTab?.statuses?.includes(updated.status);

            if (!stillInTab) {
                setOrders(prev => prev.filter(o => o.id !== order.id));
                // If it was the last item on the page, and not on page 1, go back a page
                if (orders.length === 1 && currentPage > 1) {
                    setCurrentPage(prev => prev - 1);
                }
            } else {
                // Update in local state
                setOrders(prev => prev.map(o => (o.id === order.id ? updated : o)));
            }

            if (selectedOrder?.id === order.id) {
                setSelectedOrder(updated);
            }

            // Refresh counts
            fetchTabCounts();
        } catch (err) {
            console.error('Transition failed:', err);
            setError(`Action failed: ${err?.response?.data?.message || 'Please try again.'}`);
        } finally {
            setLoadingTransition(null);
        }
    };

    const handleOrderUpdate = (updatedOrder) => {
        const stillInTab = currentTab?.statuses?.includes(updatedOrder.status);

        if (!stillInTab) {
            setOrders(prev => prev.filter(o => o.id !== updatedOrder.id));
            if (orders.length === 1 && currentPage > 1) {
                setCurrentPage(prev => prev - 1);
            }
        } else {
            setOrders(prev => prev.map(o => (o.id === updatedOrder.id ? updatedOrder : o)));
        }

        if (selectedOrder?.id === updatedOrder.id) {
            setSelectedOrder(updatedOrder);
        }
        fetchTabCounts(); // Refresh counts as re-assignment might affect them for the user
    };

    const handleBulkTransition = async (transition) => {
        setLoadingTransition('bulk');
        setError(null);
        try {
            await executeBulkTransition(selectedOrderIds, transition.publicId || transition.id, {});
            await fetchOrders();
            fetchTabCounts(); // refresh badge counts after bulk status change
        } catch (err) {
            setError(`Bulk action failed: ${err?.response?.data?.message || 'Please try again.'}`);
        } finally {
            setLoadingTransition(null);
        }
    };

    // Sum the backend per-statusCode counts across all statuses belonging to this tab
    const getTabCount = (tab) => {
        const counts = { filtered: 0, total: 0 };
        if (!tabCounts || !absoluteTabCounts) return counts;

        tab.statuses.forEach(status => {
            counts.filtered += (tabCounts[status] || 0);
            counts.total += (absoluteTabCounts[status] || 0);
        });

        return counts;
    };

    return (
        <>
            <div className="h-full flex flex-col min-h-0 bg-[#F9FAFB] relative overflow-hidden">
                {/* ── Header ── */}
                <div className="flex-shrink-0 px-6 pt-6 pb-4 bg-white border-b border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-black text-gray-900 tracking-tight">Orders Management</h1>
                            <p className="text-sm font-medium text-gray-400 mt-1">
                                {totalElements} total orders found in your account
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* View mode toggle */}
                            <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-0.5">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    title="Grid view"
                                    className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    <LayoutGrid size={16} />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    title="List view"
                                    className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    <LayoutList size={16} />
                                </button>
                                <button
                                    onClick={() => setViewMode('items')}
                                    title="Items summary"
                                    className={`p-1.5 rounded-lg transition-all ${viewMode === 'items' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    <Package size={16} />
                                </button>
                             </div>

                             {/* Loading Checklist toggle — only on items view mode and ready/in_delivery tabs */}
                             {viewMode === 'items' && (activeTab === 'ready' || activeTab === 'in_delivery') && (
                                 <button
                                     onClick={() => setChecklistMode(v => !v)}
                                     title={checklistMode ? 'Exit Checklist Mode' : 'Start Loading Checklist'}
                                     className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all duration-200 border ${
                                         checklistMode
                                             ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md border-transparent'
                                             : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200 shadow-sm'
                                     }`}
                                 >
                                     <CheckSquare size={14} />
                                     <span>Checklist</span>
                                 </button>
                             )}

                            {/* Route Planner toggle — only on In Delivery tab */}
                            {activeTab === 'in_delivery' && (
                                <button
                                    onClick={() => setRoutePlannerMode(v => !v)}
                                    title={routePlannerMode ? 'Exit Route Planner' : 'Plan Delivery Route'}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all duration-200 ${
                                        routePlannerMode
                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                                            : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100'
                                    }`}
                                >
                                    <Route size={14} />
                                    <span className="hidden sm:inline">{routePlannerMode ? 'Exit Planner' : 'Route Planner'}</span>
                                </button>
                            )}

                            <button
                                onClick={fetchOrders}
                                className={`p-2.5 rounded-xl text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-all duration-200 ${loading ? 'bg-violet-50' : ''}`}
                                title="Refresh"
                            >
                                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    </div>

                    <div className="flex overflow-x-auto gap-4 items-center no-scrollbar py-1">
                        {/* Search */}
                        <div className="relative flex-shrink-0 flex-1 min-w-[180px]">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by ID, customer name and sales person name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        {/* Date Filters */}
                        <div className="flex items-center gap-2">
                            <div className="bg-gray-100 p-1 rounded-xl flex items-center gap-1">
                                {[
                                    { id: 'TODAY', label: 'Today' },
                                    { id: 'ALL', label: 'All' },
                                    { id: 'RANGE', label: 'Range' }
                                ].map(filter => (
                                    <button
                                        key={filter.id}
                                        onClick={() => { setDateFilterType(filter.id); setUserHasSetFilter(true); }}
                                        className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${dateFilterType === filter.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>

                            {dateFilterType === 'RANGE' && (
                                <CustomDatePicker
                                    mode="range"
                                    startDate={customDateRange.start}
                                    endDate={customDateRange.end}
                                    onStartChange={(val) => setCustomDateRange(prev => ({ ...prev, start: val }))}
                                    onEndChange={(val) => setCustomDateRange(prev => ({ ...prev, end: val }))}
                                />
                            )}
                        </div>

                        {/* Grouping Filter */}
                        {isAdmin && (
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <Layers className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                    <select
                                        value={groupBy}
                                        onChange={(e) => handleGroupByChange(e.target.value)}
                                        className="pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-[11px] font-bold text-gray-900 outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all appearance-none min-w-[150px]"
                                    >
                                        <option value="none">No Grouping</option>
                                        <option value="salesperson">Group by Salesperson</option>
                                        <option value="deliveryDate">Group by Delivery Date</option>
                                    </select>
                                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                                </div>
                            </div>
                        )}

                        {/* Salesperson Filter */}
                        {isAdmin && (
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                    <select
                                        value={selectedSalesPersonId}
                                        onChange={(e) => {
                                            setSelectedSalesPersonId(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-[11px] font-bold text-gray-900 outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all appearance-none min-w-[180px]"
                                    >
                                        <option value="all">All Salespeople</option>
                                        {salespeople.map(person => (
                                            <option key={person.publicId} value={person.publicId}>
                                                {person.firstName || person.lastName 
                                                    ? `${person.firstName || ''} ${person.lastName || ''}`.trim() 
                                                    : person.username}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mt-6 overflow-x-auto no-scrollbar">
                        {TABS.map(tab => {
                            const count = getTabCount(tab);
                            const isActive = activeTab === tab.id;
                            const isFiltered = (dateFilterType !== 'ALL') || (isAdmin && selectedSalesPersonId !== 'all') || !!searchTerm;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-shrink-0 flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 relative ${isActive
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 ring-4 ring-blue-500/10'
                                        : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-100'
                                        }`}
                                >
                                    <span>{tab.label}</span>
                                    {(() => {
                                        const { filtered, total } = getTabCount(tab);
                                        const isFiltered = (dateFilterType !== 'ALL') || (isAdmin && selectedSalesPersonId !== 'all') || !!searchTerm;
                                        if (total === 0 && !isFiltered) return null;

                                        return (
                                            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-lg text-[10px] font-black ${isActive ? 'bg-white/20 text-white' : tab.countColor
                                                }`}>
                                                {isFiltered ? `${filtered} / ${total}` : total}
                                            </span>
                                        );
                                    })()}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── Error Banner ── */}
                {error && (
                    <div className="mx-6 mt-4 px-5 py-3.5 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-bold flex items-center justify-between animate-fadeIn">
                        <div className="flex items-center gap-3">
                            <X className="w-5 h-5" />
                            <span>{error}</span>
                        </div>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">✕</button>
                    </div>
                )}

                {/* ── Orders List ── */}
                <div onScroll={handleScroll} className="flex-1 overflow-y-auto px-6 pt-4 pb-32 custom-scrollbar">
                    {/* Select All Toggle */}
                    {filteredOrders.length > 0 && activeTab !== 'cancelled' && (
                        <div className="flex items-center justify-between mb-4 px-2">
                            <button
                                onClick={toggleSelectAll}
                                className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-violet-600 transition-colors"
                            >
                                {selectedOrderIds.length === totalElements && totalElements > 0 ? <CheckCircle size={16} /> : <Circle size={16} />}
                                {selectedOrderIds.length === totalElements && totalElements > 0 ? `Deselect All (${totalElements})` : `Select All (${totalElements})`}
                            </button>
                        </div>
                    )}

                    {/* ── Route Planner Panel ── */}
                    {activeTab === 'in_delivery' && routePlannerMode && (
                        <div className="mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                            {/* Action bar */}
                            <div className="flex items-center justify-between mb-4 p-4 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-200">
                                <div className="flex items-center gap-3">
                                    <Route size={18} className="text-white" />
                                    <div>
                                        <p className="text-sm font-black text-white">Route Planner</p>
                                        <p className="text-[10px] text-indigo-200 font-medium">
                                            Drag to reorder · {routeOrderedItems.length} stops
                                            {routeSequenceDirty && ' · Unsaved changes'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {savedRoute.length > 0 && (
                                        <button
                                            onClick={handleResetToDefaultRoute}
                                            disabled={savingSequence || savingRoute}
                                            title="Reset to saved default route"
                                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-all disabled:opacity-50"
                                        >
                                            <RotateCcw size={12} />
                                            <span className="hidden sm:inline">Reset Route</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={handleSaveDefaultRoute}
                                        disabled={savingRoute || savingSequence || routeOrderedItems.length === 0}
                                        title="Save as permanent default route"
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white text-xs font-bold transition-all disabled:opacity-50"
                                    >
                                        {savingRoute ? <RefreshCw size={12} className="animate-spin" /> : <Bookmark size={12} />}
                                        <span className="hidden sm:inline">Save Default Route</span>
                                    </button>
                                    <button
                                        onClick={handleSaveTodaySequence}
                                        disabled={savingSequence || savingRoute || routeOrderedItems.length === 0}
                                        title="Save today's delivery order"
                                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-indigo-600 text-xs font-black transition-all hover:scale-105 active:scale-95 shadow-lg disabled:opacity-50 disabled:scale-100"
                                    >
                                        {savingSequence ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                                        Save Today
                                    </button>
                                </div>
                            </div>
                            {/* Draggable list */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                {routeOrderedItems.length === 0 ? (
                                    <div className="py-12 text-center text-gray-400">
                                        <Route size={32} className="mx-auto mb-3 opacity-30" />
                                        <p className="text-sm font-bold">No In-Delivery orders to sequence</p>
                                    </div>
                                ) : (
                                    <div>
                                        {/* Header */}
                                        <div className="grid grid-cols-[28px_32px_1fr_auto] md:grid-cols-[32px_40px_1fr_120px_120px] gap-2 md:gap-3 px-3 md:px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                            <span className="col-span-2 text-center">#</span>
                                            <span>Customer</span>
                                            <span className="text-right">Total</span>
                                            <span className="hidden md:block text-right">Order Ref</span>
                                        </div>
                                        {routeOrderedItems.map((order, idx) => (
                                            <div
                                                key={order.id}
                                                data-route-idx={idx}
                                                draggable
                                                onDragStart={e => handleDragStart(e, idx)}
                                                onDragEnter={e => handleDragEnter(e, idx)}
                                                onDragEnd={handleDrop}
                                                onDragOver={e => e.preventDefault()}
                                                onTouchStart={e => handleTouchStart(e, idx)}
                                                onTouchMove={handleTouchMove}
                                                onTouchEnd={handleTouchEnd}
                                                className="grid grid-cols-[28px_32px_1fr_auto] md:grid-cols-[32px_40px_1fr_120px_120px] gap-2 md:gap-3 px-3 md:px-4 py-3 items-center cursor-grab active:cursor-grabbing hover:bg-indigo-50/60 transition-colors border-b border-gray-50 last:border-0 select-none touch-none"
                                            >
                                                {/* Drag handle */}
                                                <div className="flex items-center justify-center text-gray-300 hover:text-gray-500">
                                                    <GripVertical size={16} />
                                                </div>
                                                {/* Sequence badge */}
                                                <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-black shadow-sm shadow-indigo-200 shrink-0">
                                                    {idx + 1}
                                                </div>
                                                {/* Customer info */}
                                                <div className="min-w-0 pr-1">
                                                    <p className="text-sm font-bold text-gray-900 leading-snug break-words">{order.customer?.name || '—'}</p>
                                                    <p className="text-[11px] text-gray-400 font-medium truncate">
                                                        <span className="text-indigo-600 font-bold">{order.orderRef || `#${(order.id || '').slice(-6)}`}</span>
                                                        {order.customer?.address || order.customer?.phone ? ` · ${order.customer?.address || order.customer?.phone}` : ''}
                                                    </p>
                                                </div>
                                                {/* Total */}
                                                <div className="text-right shrink-0">
                                                    <span className="text-sm font-black text-gray-900">{formatCurrency(order.totalAmount)}</span>
                                                </div>
                                                {/* Order ref (desktop) */}
                                                <div className="text-right hidden md:block shrink-0">
                                                    <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-lg">{order.orderRef || `#${(order.id || '').slice(-6)}`}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32">
                            <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-6" />
                            <p className="text-lg font-bold text-gray-400">Loading your orders...</p>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className={`flex flex-col items-center justify-center text-center bg-white rounded-3xl border border-dashed border-gray-200 ${activeTab === 'new_order' && onNavigate && !searchTerm ? 'py-12' : 'py-20'}`}>
                            {activeTab === 'new_order' && onNavigate && !searchTerm ? (
                                /* Special new-order empty state — compact so button is always visible */
                                <>
                                    <div className="w-20 h-20 rounded-3xl bg-blue-50 flex items-center justify-center mb-4">
                                        <ShoppingBag size={40} className="text-blue-300" />
                                    </div>
                                    <h3 className="text-xl font-black text-gray-900 mb-1">No orders yet</h3>
                                    <p className="text-sm font-medium text-gray-400 max-w-xs mx-auto mb-6">
                                        Start a new order from the POS or customer portal.
                                    </p>
                                    <button
                                        onClick={() => onNavigate('pos')}
                                        className="px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-black rounded-2xl shadow-xl shadow-blue-200 hover:scale-105 active:scale-95 transition-all"
                                    >
                                        START NEW ORDER
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="w-24 h-24 rounded-3xl bg-gray-50 flex items-center justify-center mb-6">
                                        <ShoppingBag size={48} className="text-gray-200" />
                                    </div>
                                    <h3 className="text-xl font-black text-gray-900 mb-2">No orders here</h3>
                                    <p className="text-sm font-medium text-gray-400 max-w-xs mx-auto">
                                        {searchTerm ? `We couldn't find any orders matching "${searchTerm}"` : `There are currently no orders in the ${currentTab?.label} stage.`}
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        Object.entries(groupedOrders).map(([groupName, groupOrders]) => {
                            const isCollapsed = isGroupingActive && collapsedGroups[groupName] !== false;
                            const totalAmountSum = groupOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
                            const orderCount = groupOrders.length;

                            // Inline items computation if viewMode is 'items'
                            const itemsSummary = (() => {
                                if (viewMode !== 'items') return [];
                                const summary = {};
                                groupOrders.forEach(order => {
                                    (order.items || []).forEach(item => {
                                        const name = item.name || item.productName || item.product?.name || 'Unknown Product';
                                        const unit = item.unit || 'pcs';
                                        const key = `${name}_${unit}`;
                                        if (!summary[key]) {
                                            summary[key] = {
                                                name,
                                                unit,
                                                quantity: 0,
                                                totalPrice: 0,
                                                ordersRefs: new Set()
                                            };
                                        }
                                        summary[key].quantity += (item.quantity || 0);
                                        summary[key].totalPrice += (item.total || item.totalPrice || 0);
                                        summary[key].ordersRefs.add(order.orderRef || `#${order.id?.slice(-8)}`);
                                    });
                                });
                                return Object.values(summary).sort((a, b) => b.quantity - a.quantity);
                            })();

                            return (
                                <div key={groupName} className="mb-8">
                                    {isGroupingActive && (
                                        <div
                                            onClick={() => setCollapsedGroups(prev => ({
                                                ...prev,
                                                [groupName]: prev[groupName] === false ? true : false
                                            }))}
                                            className="flex items-center justify-between p-4 mb-4 bg-white border border-gray-100/80 rounded-2xl shadow-sm hover:shadow-md cursor-pointer select-none transition-all duration-300 hover:border-blue-100"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`p-1.5 rounded-xl transition-transform duration-300 ${isCollapsed ? '' : 'rotate-90'}`}>
                                                    <ChevronRight size={16} className="text-gray-400" />
                                                </div>

                                                {/* Avatar / Icon */}
                                                {groupBy === 'salesperson' && (
                                                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-black text-xs shrink-0 border border-blue-100">
                                                        {groupName === 'Unassigned' ? '?' : groupName.substring(0, 2).toUpperCase()}
                                                    </div>
                                                )}
                                                {groupBy === 'deliveryDate' && (
                                                    <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0 border border-indigo-100">
                                                        <CalendarIcon size={14} />
                                                    </div>
                                                )}

                                                <div>
                                                    <h3 className="text-xs font-black text-gray-900 tracking-tight uppercase tracking-wider">
                                                        {groupName}
                                                    </h3>
                                                    <p className="text-[10px] text-gray-400 font-bold mt-0.5">
                                                        {orderCount} {orderCount === 1 ? 'Order' : 'Orders'} · <span className="text-blue-600">{formatCurrency(totalAmountSum)}</span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!isCollapsed && (
                                        viewMode === 'grid' ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                                                {groupOrders.map(order => (
                                                    <SalesOrderCard
                                                        key={order.id}
                                                        order={order}
                                                        isSelectionAllowed={activeTab !== 'cancelled'}
                                                        isSelected={selectedOrderIds.includes(order.id)}
                                                        onSelect={toggleSelect}
                                                        transitions={getAvailableTransitionsForOrder(order)}
                                                        onTransition={handleTransition}
                                                        onRecordSale={setRecordSaleOrder}
                                                        loadingTransition={loadingTransition === order.id}
                                                        onViewDetail={setSelectedOrder}
                                                        canEdit={canEditOrder(order)}
                                                        onEdit={setEditModalOrder}
                                                        deliverySequence={activeTab === 'in_delivery' ? order.deliverySequence : undefined}
                                                    />
                                                ))}
                                            </div>
                                        ) : viewMode === 'list' ? (
                                            /* ─── List / Table mode ─── */
                                            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                                                {/* Mobile PWA List View (< md) */}
                                                <div className="md:hidden divide-y divide-gray-100">
                                                    {groupOrders.map((order) => {
                                                        const rawTransitions = getAvailableTransitionsForOrder(order);
                                                        const canEdit = canEditOrder(order);
                                                        const isModificationRequired = order.status === 'REQUEST_FOR_MODIFICATION';
                                                        const itemsList = order.items || [];
                                                        const hasAnyRevision = itemsList.some(i => i.warehouseQuantity !== null && i.warehouseQuantity !== undefined && Number(i.warehouseQuantity) !== Number(i.quantity));

                                                        const rowTransitions = rawTransitions.reduce((uniq, t) => {
                                                            const rawLabel = t.transitionName || t.label || t.name || t.action || '';
                                                            let cleanLabel = rawLabel;
                                                            if (rawLabel.toLowerCase().includes('send to warehouse')) cleanLabel = 'To Warehouse';
                                                            if (rawLabel.toLowerCase().includes('deliver')) cleanLabel = 'Deliver';
                                                            
                                                            const isResubmit = rawLabel.toLowerCase().includes('accept revision') || rawLabel.toLowerCase().includes('confirm revision') || rawLabel.toLowerCase().includes('resubmit');
                                                            if (isResubmit) cleanLabel = 'Resubmit';

                                                            if (isResubmit && isModificationRequired && hasAnyRevision && !canEdit) {
                                                                return uniq;
                                                            }

                                                            if (!uniq.some(ext => {
                                                                const extLabel = ext.transitionName || ext.label || ext.name || ext.action || '';
                                                                const extIsRes = extLabel.toLowerCase().includes('accept revision') || extLabel.toLowerCase().includes('confirm revision') || extLabel.toLowerCase().includes('resubmit');
                                                                const cleanExtLabel = extIsRes ? 'Resubmit' : extLabel;
                                                                return cleanExtLabel === cleanLabel;
                                                            })) {
                                                                uniq.push(t);
                                                            }
                                                            return uniq;
                                                        }, []);

                                                        const totalItemsCount = order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

                                                        return (
                                                            <div
                                                                key={order.id}
                                                                onClick={() => setSelectedOrder(order)}
                                                                className="p-4 hover:bg-blue-50/40 active:bg-blue-50/70 transition-colors cursor-pointer space-y-2.5"
                                                            >
                                                                {/* Top Row: Checkbox/Seq + Customer Name + Total */}
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                                                        {activeTab !== 'cancelled' && (
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); toggleSelect(order.id); }}
                                                                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all mt-0.5 shrink-0 ${selectedOrderIds.includes(order.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-transparent hover:border-blue-300'}`}
                                                                            >
                                                                                <Check size={12} strokeWidth={3} />
                                                                            </button>
                                                                        )}
                                                                        {activeTab === 'in_delivery' && order.deliverySequence != null && (
                                                                            <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[9px] font-black mt-0.5">
                                                                                {order.deliverySequence}
                                                                            </span>
                                                                        )}
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="text-sm font-bold text-gray-900 leading-snug break-words">
                                                                                {order.customer?.name || '—'}
                                                                            </p>
                                                                            <p className="text-[11px] text-gray-400 font-medium mt-0.5">
                                                                                {order.orderRef || `#${(order.id || '').slice(-8)}`} {order.salesman ? `· ${order.salesman}` : ''}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    {/* Total Amount & Items badge */}
                                                                    <div className="text-right shrink-0">
                                                                        <span className="text-sm font-black text-gray-900 block">{formatCurrency(order.totalAmount)}</span>
                                                                        <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md inline-block mt-0.5">
                                                                            {totalItemsCount} {totalItemsCount === 1 ? 'item' : 'items'}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Bottom Row: Status Pill + Delivery Info + Action buttons */}
                                                                <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-50">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold ${getStatusColor(order.status)}`}>
                                                                            {getStatusLabel(order.status)}
                                                                        </span>
                                                                        {order.deliveryDate && (
                                                                            <span className="text-[10px] font-black text-emerald-600 flex items-center gap-1">
                                                                                <CalendarIcon size={10} />
                                                                                {formatDateTime(order.deliveryDate, { hour: undefined, minute: undefined })}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {/* Quick action buttons */}
                                                                    <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                                                        {canEdit && (
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); setEditModalOrder(order); }}
                                                                                disabled={!!loadingTransition}
                                                                                className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-amber-50 hover:bg-amber-100 text-amber-600 border border-amber-100 transition-all disabled:opacity-50"
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                        )}
                                                                        {rowTransitions.slice(0, 2).map(t => {
                                                                            const rawLabel = t.transitionName || t.label || t.name || t.action || 'Action';
                                                                            let cleanLabel = rawLabel;
                                                                            if (rawLabel.toLowerCase().includes('send to warehouse')) cleanLabel = 'To Warehouse';
                                                                            if (rawLabel.toLowerCase().includes('deliver')) cleanLabel = 'Deliver';
                                                                            
                                                                            const isResubmit = rawLabel.toLowerCase().includes('accept revision') || rawLabel.toLowerCase().includes('confirm revision') || rawLabel.toLowerCase().includes('resubmit');
                                                                            if (isResubmit) cleanLabel = 'Resubmit';

                                                                            const isDisabled = !!loadingTransition || (isResubmit && isModificationRequired && hasAnyRevision);

                                                                            let btnStyle = 'bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-gray-600';
                                                                            if (isResubmit) {
                                                                                btnStyle = 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm';
                                                                            } else if (cleanLabel.toLowerCase().includes('cancel') || cleanLabel.toLowerCase().includes('reject')) {
                                                                                btnStyle = 'bg-red-50 hover:bg-red-100 hover:text-red-700 text-red-600 border border-red-100';
                                                                            }

                                                                            return (
                                                                                <button
                                                                                    key={t.publicId || t.id}
                                                                                    onClick={() => handleTransition(order, t)}
                                                                                    disabled={isDisabled}
                                                                                    className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all disabled:opacity-50 whitespace-nowrap ${btnStyle}`}
                                                                                >
                                                                                    {cleanLabel}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Desktop Table View (md:block) */}
                                                <div className="hidden md:block overflow-x-auto custom-scrollbar">
                                                    <div className="min-w-[800px]">
                                                        {/* List header */}
                                                        <div className={`grid ${activeTab !== 'cancelled' ? 'grid-cols-[24px_minmax(200px,1.5fr)_70px_125px_140px_220px]' : 'grid-cols-[minmax(200px,1.5fr)_70px_125px_140px_220px]'} gap-4 px-5 py-3 bg-gray-50 border-b border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400`}>
                                                            {activeTab !== 'cancelled' && <span />}
                                                            <span>Customer</span>
                                                            <span className="text-right">Items</span>
                                                            <span className="text-right">Total (Incl. VAT)</span>
                                                            <span className="text-right">Updated</span>
                                                            <span className="text-right">Actions</span>
                                                        </div>
                                                        {groupOrders.map((order, idx) => {
                                                             const rawTransitions = getAvailableTransitionsForOrder(order);
                                                             const canEdit = canEditOrder(order);
                                                             const isModificationRequired = order.status === 'REQUEST_FOR_MODIFICATION';
                                                             const itemsList = order.items || [];
                                                             const hasAnyRevision = itemsList.some(i => i.warehouseQuantity !== null && i.warehouseQuantity !== undefined && Number(i.warehouseQuantity) !== Number(i.quantity));

                                                             const rowTransitions = rawTransitions.reduce((uniq, t) => {
                                                                 const rawLabel = t.transitionName || t.label || t.name || t.action || '';
                                                                 let cleanLabel = rawLabel;
                                                                 if (rawLabel.toLowerCase().includes('send to warehouse')) cleanLabel = 'To Warehouse';
                                                                 if (rawLabel.toLowerCase().includes('deliver')) cleanLabel = 'Deliver';
                                                                 
                                                                 const isResubmit = rawLabel.toLowerCase().includes('accept revision') || rawLabel.toLowerCase().includes('confirm revision') || rawLabel.toLowerCase().includes('resubmit');
                                                                 if (isResubmit) cleanLabel = 'Resubmit';

                                                                 // Hide Resubmit if revisions exist and user cannot edit
                                                                 if (isResubmit && isModificationRequired && hasAnyRevision && !canEdit) {
                                                                     return uniq;
                                                                 }

                                                                 if (!uniq.some(ext => {
                                                                     const extLabel = ext.transitionName || ext.label || ext.name || ext.action || '';
                                                                     const extIsRes = extLabel.toLowerCase().includes('accept revision') || extLabel.toLowerCase().includes('confirm revision') || extLabel.toLowerCase().includes('resubmit');
                                                                     const cleanExtLabel = extIsRes ? 'Resubmit' : extLabel;
                                                                     return cleanExtLabel === cleanLabel;
                                                                 })) {
                                                                     uniq.push(t);
                                                                 }
                                                                 return uniq;
                                                             }, []);

                                                             return (
                                                                 <div
                                                                     key={order.id}
                                                                     onClick={() => setSelectedOrder(order)}
                                                                     className={`grid ${activeTab !== 'cancelled' ? 'grid-cols-[24px_minmax(200px,1.5fr)_70px_125px_140px_220px]' : 'grid-cols-[minmax(200px,1.5fr)_70px_125px_140px_220px]'} gap-4 px-5 py-3.5 items-center cursor-pointer hover:bg-blue-50/50 transition-colors ${idx < groupOrders.length - 1 ? 'border-b border-gray-50' : ''}`}
                                                                 >
                                                                     {/* Checkbox */}
                                                                     {activeTab !== 'cancelled' && (
                                                                         <button
                                                                             onClick={(e) => { e.stopPropagation(); toggleSelect(order.id); }}
                                                                             className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${selectedOrderIds.includes(order.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-transparent hover:border-blue-300'}`}
                                                                         >
                                                                             <Check size={12} strokeWidth={3} />
                                                                         </button>
                                                                     )}
                                                                     {/* Customer + order ref */}
                                                                     <div className="min-w-0 pr-1">
                                                                         <div className="flex items-center gap-2">
                                                                             {/* Sequence badge for In Delivery */}
                                                                             {activeTab === 'in_delivery' && order.deliverySequence != null && (
                                                                                 <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[9px] font-black">
                                                                                     {order.deliverySequence}
                                                                                 </span>
                                                                             )}
                                                                             <span className="text-sm font-bold text-gray-900 break-words leading-tight" title={order.customer?.name || '—'}>
                                                                                 {order.customer?.name || '—'}
                                                                             </span>
                                                                         </div>
                                                                         <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-400 font-medium min-w-0">
                                                                             <span className="truncate">
                                                                                 {order.orderRef || `#${(order.id || '').slice(-8)}`} · {order.salesman}
                                                                             </span>
                                                                             <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold ${getStatusColor(order.status)} shrink-0`}>
                                                                                 {getStatusLabel(order.status)}
                                                                             </span>
                                                                         </div>
                                                                     </div>
                                                                     {/* Items count */}
                                                                     <span className="text-xs font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg text-right whitespace-nowrap">
                                                                         {order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                                                                     </span>
                                                                     {/* Total */}
                                                                     <span className="text-sm font-black text-gray-900 text-right whitespace-nowrap">
                                                                         {formatCurrency(order.totalAmount)}
                                                                     </span>
                                                                     {/* Updated At */}
                                                                     <div className="text-right min-w-0">
                                                                         {order.deliveryDate ? (
                                                                             <p className="text-[10px] font-black text-emerald-600 flex items-center justify-end gap-1">
                                                                                 <CalendarIcon size={9} />
                                                                                 {formatDateTime(order.deliveryDate, { hour: undefined, minute: undefined })}
                                                                             </p>
                                                                         ) : (
                                                                             <p className="text-[11px] font-bold text-gray-700">
                                                                                 {formatDateTime(order.updatedAt || order.createdAt)}
                                                                             </p>
                                                                         )}
                                                                         {order.timeSlot && (
                                                                             <p className="text-[9px] text-emerald-500 font-medium truncate">
                                                                                 {order.timeSlot}
                                                                             </p>
                                                                         )}
                                                                     </div>
                                                                     {/* Quick action buttons */}
                                                                     <div className="flex items-center gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                                                                         {canEdit && (
                                                                             <button
                                                                                 onClick={(e) => { e.stopPropagation(); setEditModalOrder(order); }}
                                                                                 disabled={!!loadingTransition}
                                                                                 title="Edit Order"
                                                                                 className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-amber-50 hover:bg-amber-100 hover:text-amber-700 text-amber-600 border border-amber-100 transition-all disabled:opacity-50"
                                                                             >
                                                                                 Edit
                                                                             </button>
                                                                         )}
                                                                         {rowTransitions.slice(0, 2).map(t => {
                                                                             const rawLabel = t.transitionName || t.label || t.name || t.action || 'Action';
                                                                             let cleanLabel = rawLabel;
                                                                             if (rawLabel.toLowerCase().includes('send to warehouse')) cleanLabel = 'To Warehouse';
                                                                             if (rawLabel.toLowerCase().includes('deliver')) cleanLabel = 'Deliver';
                                                                             
                                                                             const isResubmit = rawLabel.toLowerCase().includes('accept revision') || rawLabel.toLowerCase().includes('confirm revision') || rawLabel.toLowerCase().includes('resubmit');
                                                                             if (isResubmit) cleanLabel = 'Resubmit';

                                                                             const isDisabled = !!loadingTransition || (isResubmit && isModificationRequired && hasAnyRevision);

                                                                             // Determine styling
                                                                             let btnStyle = 'bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-gray-600';
                                                                             if (isResubmit) {
                                                                                 btnStyle = 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm';
                                                                             } else if (cleanLabel.toLowerCase().includes('cancel') || cleanLabel.toLowerCase().includes('reject')) {
                                                                                 btnStyle = 'bg-red-50 hover:bg-red-100 hover:text-red-700 text-red-600 border border-red-100';
                                                                             }

                                                                             return (
                                                                                 <button
                                                                                     key={t.publicId || t.id}
                                                                                     onClick={() => handleTransition(order, t)}
                                                                                     disabled={isDisabled}
                                                                                     title={isDisabled && isResubmit ? "Address modifications by editing the order first" : cleanLabel}
                                                                                     className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all disabled:opacity-50 whitespace-nowrap ${btnStyle}`}
                                                                                 >
                                                                                     {cleanLabel}
                                                                                 </button>
                                                                             );
                                                                         })}
                                                                     </div>
                                                                 </div>
                                                             );
                                                         })}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* ─── Items Summary Mode ─── */
                                            <>
                                                {/* Checklist Progress Banner */}
                                                {checklistMode && itemsSummary.length > 0 && (() => {
                                                    const totalItemsCount = itemsSummary.length;
                                                    const fullyVerifiedItemsCount = itemsSummary.filter(item => {
                                                        const key = `${item.name}_${item.unit}`;
                                                        return (verifiedQuantities[key] || 0) === item.quantity;
                                                    }).length;

                                                    const loadingPercentage = totalItemsCount > 0 ? Math.round((fullyVerifiedItemsCount / totalItemsCount) * 100) : 0;

                                                    return (
                                                        <div className="bg-gradient-to-r from-blue-50/70 to-indigo-50/70 border border-blue-100/80 rounded-3xl p-4 mb-4 flex flex-col gap-3 shadow-sm animate-fadeIn">
                                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                                <div>
                                                                    <h4 className="text-xs font-black text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                                                                        <Truck size={14} className="text-blue-600 animate-pulse" />
                                                                        Vehicle Loading Checklist
                                                                    </h4>
                                                                    <p className="text-[10px] text-gray-500 font-bold mt-0.5">
                                                                        Verify and tick off items as you load them onto the vehicle.
                                                                    </p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={resetChecklist}
                                                                        className="px-3 py-1.5 bg-white text-gray-600 hover:text-red-600 rounded-xl text-[10px] font-black uppercase tracking-wider border border-gray-200/60 shadow-sm transition-all"
                                                                    >
                                                                        Reset
                                                                    </button>
                                                                    {loadingPercentage === 100 && (
                                                                        <span className="px-3 py-1.5 bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-wider shadow-md shadow-emerald-100 flex items-center gap-1 animate-bounce">
                                                                            <Check size={10} strokeWidth={3} /> Loaded & Verified
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="space-y-1.5">
                                                                <div className="flex justify-between text-[10px] font-black uppercase tracking-wider text-blue-800">
                                                                    <span>Loading Progress</span>
                                                                    <span>{fullyVerifiedItemsCount} / {totalItemsCount} Products ({loadingPercentage}%)</span>
                                                                </div>
                                                                <div className="w-full bg-blue-100/50 rounded-full h-2 overflow-hidden border border-blue-200/10">
                                                                    <div 
                                                                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full rounded-full transition-all duration-500"
                                                                        style={{ width: `${loadingPercentage}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                                                    <div className="overflow-x-auto custom-scrollbar">
                                                        <div className="min-w-[600px]">
                                                            {/* List header */}
                                                            <div className={`grid ${checklistMode ? 'grid-cols-[1fr_210px_110px_180px]' : 'grid-cols-[1fr_95px_110px_180px]'} gap-4 px-5 py-3 bg-gray-50 border-b border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400`}>
                                                                <span>Product Name</span>
                                                                <span className="text-center">{checklistMode ? 'Loading Verification' : 'Total Quantity'}</span>
                                                                <span className="text-right">Total Price</span>
                                                                <span className="text-right">Related Orders</span>
                                                            </div>
                                                            {itemsSummary.map((item, idx) => {
                                                                const itemKey = `${item.name}_${item.unit}`;
                                                                const verifiedQty = verifiedQuantities[itemKey] || 0;
                                                                const isFullyVerified = verifiedQty === item.quantity;
                                                                const isPartiallyVerified = verifiedQty > 0 && verifiedQty < item.quantity;

                                                                const handleDecrement = (e) => {
                                                                    e.stopPropagation();
                                                                    const nextVal = Math.max(0, verifiedQty - 1);
                                                                    updateVerifiedQty(itemKey, nextVal);
                                                                };

                                                                const handleIncrement = (e) => {
                                                                    e.stopPropagation();
                                                                    const nextVal = Math.min(item.quantity, verifiedQty + 1);
                                                                    updateVerifiedQty(itemKey, nextVal);
                                                                };

                                                                const handleQuickCheck = (e) => {
                                                                    e.stopPropagation();
                                                                    if (isFullyVerified) {
                                                                        updateVerifiedQty(itemKey, 0);
                                                                    } else {
                                                                        updateVerifiedQty(itemKey, item.quantity);
                                                                    }
                                                                };

                                                                return (
                                                                    <div
                                                                        key={`${item.name}_${item.unit}`}
                                                                        className={`grid ${checklistMode ? 'grid-cols-[1fr_210px_110px_180px]' : 'grid-cols-[1fr_95px_110px_180px]'} gap-4 px-5 py-3.5 items-center hover:bg-blue-50/30 transition-colors ${idx < itemsSummary.length - 1 ? 'border-b border-gray-50' : ''}`}
                                                                    >
                                                                        <span className="font-bold text-gray-900 text-sm break-words whitespace-normal" title={item.name}>
                                                                            {item.name}
                                                                        </span>
                                                                        <div className="text-center">
                                                                            {checklistMode ? (
                                                                                <div className="flex items-center gap-1.5 justify-center" onClick={(e) => e.stopPropagation()}>
                                                                                    <button
                                                                                        onClick={handleDecrement}
                                                                                        className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-sm transition-all"
                                                                                    >
                                                                                        -
                                                                                    </button>
                                                                                    <span className={`inline-flex items-center justify-center min-w-[5.2rem] px-2.5 py-1 rounded-lg text-xs font-black border transition-all ${
                                                                                        isFullyVerified 
                                                                                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                                                                                            : isPartiallyVerified 
                                                                                                ? 'bg-amber-50 text-amber-800 border-amber-200 shadow-sm' 
                                                                                                : 'bg-gray-50 text-gray-400 border-gray-200'
                                                                                    }`}>
                                                                                        {verifiedQty} / {item.quantity} <span className="text-[9px] ml-1 uppercase text-gray-500">{item.unit}</span>
                                                                                    </span>
                                                                                    <button
                                                                                        onClick={handleIncrement}
                                                                                        className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center font-bold text-sm transition-all"
                                                                                    >
                                                                                        +
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={handleQuickCheck}
                                                                                        className={`ml-1 w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                                                                                            isFullyVerified 
                                                                                                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-100' 
                                                                                                : 'bg-gray-50 hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 border border-gray-200'
                                                                                        }`}
                                                                                    >
                                                                                        <Check size={13} strokeWidth={3.5} />
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="inline-flex items-center justify-center min-w-[3rem] px-2.5 py-1 rounded-lg bg-blue-50 text-blue-800 font-black text-xs border border-blue-100">
                                                                                    {item.quantity} <span className="text-[9px] ml-1 uppercase text-blue-600 font-bold">{item.unit}</span>
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-sm font-black text-gray-950 text-right whitespace-nowrap">
                                                                            {formatCurrency(item.totalPrice)}
                                                                        </span>
                                                                        <div className="flex flex-wrap justify-end gap-1 max-w-[200px] ml-auto">
                                                                            {Array.from(item.ordersRefs).slice(0, 3).map(ref => (
                                                                                <span key={ref} className="text-[9px] bg-gray-50 text-gray-600 px-2 py-0.5 rounded border border-gray-200/50 font-mono">
                                                                                    {ref}
                                                                                </span>
                                                                            ))}
                                                                            {item.ordersRefs.size > 3 && (
                                                                                <span className="text-[9px] text-gray-400 font-bold px-1 py-0.5">
                                                                                    +{item.ordersRefs.size - 3} more
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        {itemsSummary.length === 0 && (
                                                            <div className="py-12 text-center text-gray-400 font-medium">
                                                                No items found.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            </>
                                        )
                                    )}
                                </div>
                            );
                        })
                    )}
                    {/* Loading More Spinner */}
                    {loadingMore && (
                        <div className="flex justify-center py-6">
                            <div className="w-8 h-8 border-3 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
                        </div>
                    )}
                </div>

                {/* ── Bulk Action Bar (Sticky) ── */}
                {selectedOrderIds.length > 0 && (
                    <div className="fixed bottom-24 md:bottom-12 left-1/2 -translate-x-1/2 z-[100] animate-slideUp w-[calc(100%-1.5rem)] md:w-auto max-w-lg md:max-w-none">
                        <div className="bg-gray-900/95 border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] rounded-2xl md:rounded-[2rem] px-4 py-3 md:px-8 md:py-4 flex items-center gap-4 md:gap-8 ring-1 ring-white/20 backdrop-blur-2xl">
                            <div className="flex items-center gap-3 md:gap-5 pr-4 md:pr-8 border-r border-white/10 flex-shrink-0">
                                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-blue-600 flex items-center justify-center text-white font-black text-sm md:text-lg shadow-lg shadow-blue-500/30">
                                    {selectedOrderIds.length}
                                </div>
                                <div>
                                    <p className="text-[10px] md:text-xs font-black text-white uppercase tracking-widest">Selected</p>
                                    <button
                                        onClick={() => setSelectedOrderIds([])}
                                        className="text-[9px] md:text-[10px] font-bold text-gray-400 hover:text-white transition-colors"
                                    >
                                        Clear selection
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto no-scrollbar flex-1 min-w-0">
                                {commonTransitions.length > 0 ? (
                                    commonTransitions.map(t => {
                                        const rawLabel = t.transitionName || t.label || t.name || t.action || '';
                                        let cleanLabel = rawLabel;
                                        if (rawLabel.toLowerCase().includes('send to warehouse')) cleanLabel = 'To Warehouse';
                                        if (rawLabel.toLowerCase().includes('deliver')) cleanLabel = 'Deliver';
                                        if (rawLabel.toLowerCase().includes('accept revision') || rawLabel.toLowerCase().includes('confirm revision') || rawLabel.toLowerCase().includes('resubmit')) cleanLabel = 'Resubmit';

                                        return (
                                            <button
                                                key={t.publicId || t.id}
                                                onClick={() => handleBulkTransition(t)}
                                                disabled={loadingTransition === 'bulk'}
                                                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 md:px-5 md:py-3 rounded-xl md:rounded-2xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${getBulkTransitionStyle(t)
                                                    }`}
                                            >
                                                {loadingTransition === 'bulk' ? (
                                                    <RefreshCw size={14} className="animate-spin" />
                                                ) : (
                                                    <>
                                                        {getTransitionIcon(t)}
                                                        {cleanLabel}
                                                    </>
                                                )}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <p className="text-[11px] font-bold text-gray-500 px-4">
                                        No common actions available
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {selectedOrder && (
                <OrderDetailDrawer
                    order={selectedOrder}
                    transitions={getAvailableTransitionsForOrder(selectedOrder)}
                    onTransition={handleTransition}
                    onOrderUpdate={handleOrderUpdate}
                    onRecordSale={(ord) => {
                        setSelectedOrder(null);
                        setRecordSaleOrder(ord);
                    }}
                    loadingTransition={loadingTransition === selectedOrder.id}
                    onClose={() => setSelectedOrder(null)}
                    role={role}
                    onEditOrder={canEditOrder(selectedOrder) ? setEditModalOrder : null}
                />
            )}

            {recordSaleOrder && (
                <RecordSaleModal
                    order={recordSaleOrder}
                    isOpen={!!recordSaleOrder}
                    onClose={() => setRecordSaleOrder(null)}
                    onSuccess={async () => {
                        await fetchOrders();
                        fetchTabCounts();
                    }}
                />
            )}

            <EditOrderModal
                isOpen={!!editModalOrder}
                order={editModalOrder}
                currentRole={role}
                onClose={() => setEditModalOrder(null)}
                onSuccess={async () => {
                    setEditModalOrder(null);
                    await fetchOrders();
                    fetchTabCounts();
                }}
            />

            <CancelOrderModal
                isOpen={!!cancelModalOrder}
                order={cancelModalOrder}
                transition={cancelModalTransition}
                onClose={() => {
                    setCancelModalOrder(null);
                    setCancelModalTransition(null);
                }}
                onSubmit={handleCancelSubmit}
                loading={loadingTransition === cancelModalOrder?.id}
            />
        </>
    );
};

const getBulkTransitionStyle = (transition) => {
    const name = (transition.transitionName || transition.label || transition.name || transition.action || '').toLowerCase();
    if (name.includes('cancel') || name.includes('reject') || name.includes('return')) {
        return 'bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white ring-1 ring-red-500/20';
    }
    if (name.includes('warehouse') || name.includes('send') || name.includes('approve') || name.includes('resubmit')) {
        return 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-900/40 hover:scale-105';
    }
    return 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/40 hover:scale-105';
};

const getTransitionIcon = (transition) => {
    const name = (transition.transitionName || transition.label || transition.name || transition.action || '').toLowerCase();
    if (name.includes('cancel')) return <Trash2 size={14} strokeWidth={2.5} />;
    if (name.includes('reject') || name.includes('return')) return <RefreshCw size={14} strokeWidth={2.5} />;
    if (name.includes('warehouse') || name.includes('send') || name.includes('resubmit')) return <Package size={14} strokeWidth={2.5} />;
    if (name.includes('approve')) return <Check size={14} strokeWidth={2.5} />;
    if (name.includes('dispatch') || name.includes('deliver')) return <Clock size={14} strokeWidth={2.5} />;
    return <ArrowRight size={14} strokeWidth={2.5} />;
};

export default SalesOrdersView;
